import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { pageTransitionVariants, useSnapVariants } from './lib/motion';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import LandingPage from './pages/LandingPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Header from './components/layout/Header';
import BottomNav from './components/layout/BottomNav';
import BottomSheet from './components/layout/BottomSheet';
import MoreSheet from './components/layout/MoreSheet';
import { ToastContainer } from './components/ui/Toast';
import PWAInstaller from './components/ui/PWAInstaller';
import Spinner from './components/ui/Spinner';
import { useAuthStore } from './store/authStore';
import { ShortcutsHelp } from './components/ui/ShortcutsHelp';
import { OfflineBanner } from './components/ui/OfflineBanner';

const TemplateSelector = lazy(() => import('./components/ui/TemplateSelector'));
const EditorPage = lazy(() => import('./pages/EditorPage'));
const ExportPage = lazy(() => import('./pages/ExportPage'));
const AlbumsPage = lazy(() => import('./pages/AlbumsPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const GalleryDetailPage = lazy(() => import('./pages/GalleryDetailPage'));
const BookmarkDetailPage = lazy(() => import('./pages/BookmarkDetailPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SharedAlbumPage = lazy(() => import('./pages/SharedAlbumPage'));
const SubscriptionSuccessPage = lazy(() => import('./pages/SubscriptionSuccessPage'));
const SubscriptionCancelPage = lazy(() => import('./pages/SubscriptionCancelPage'));
const EmailVerifyPage = lazy(() => import('./pages/EmailVerifyPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const TeamsPage = lazy(() => import('./pages/TeamsPage'));
const CreditsPage = lazy(() => import('./pages/CreditsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const FeedPage = lazy(() => import('./pages/FeedPage'));
const MigrationPage = lazy(() => import('./pages/MigrationPage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));
const TemplateDetailPage = lazy(() => import('./pages/TemplateDetailPage'));
const TemplateUploadPage = lazy(() => import('./pages/TemplateUploadPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const CollectionsPage = lazy(() => import('./pages/CollectionsPage'));
const CollectionDetailPage = lazy(() => import('./pages/CollectionDetailPage'));
const BookmarksPage = lazy(() => import('./pages/BookmarksPage'));

/**
 * 기본 테마 설정
 */
const theme = createTheme({
  palette: {
    primary: {
      main: '#3b82f6',
    },
    secondary: {
      main: '#10b981',
    },
  },
  typography: {
    fontFamily: [
      'Pretendard',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

/**
 * 루트 경로 리다이렉트 컴포넌트
 * - 로그인 상태에 따라 적절한 페이지로 리다이렉트
 */
const RootRedirect: React.FC = () => {
  const { isAuthenticated } = useAuthStore();

  // 로그인된 경우 앨범 페이지로, 아니면 랜딩 페이지
  if (isAuthenticated) {
    return <Navigate to="/albums" replace />;
  }
  return <LandingPage />;
};

/** BottomNav를 숨겨야 하는 focus-mode 경로 패턴 */
const FOCUS_MODE_PATTERNS = [
  /^\/albums\/[^/]+\/edit/,
  /^\/albums\/[^/]+\/export/,
  /^\/login/,
  /^\/signup/,
  /^\/auth\//,
];

/**
 * AppShell — BottomNav, FAB, BottomSheet 관리
 */
const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);

  const isFocusMode = FOCUS_MODE_PATTERNS.some((p) => p.test(location.pathname));
  const pageVariants = useSnapVariants(pageTransitionVariants);

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          variants={pageVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {children}
        </motion.div>
      </AnimatePresence>
      {!isFocusMode && (
        <BottomNav
          onCreateClick={() => setBottomSheetOpen(true)}
          onMoreClick={() => setMoreSheetOpen(true)}
        />
      )}
      <BottomSheet
        open={bottomSheetOpen}
        onClose={() => setBottomSheetOpen(false)}
        onOpenTemplate={() => setTemplateSelectorOpen(true)}
      />
      <MoreSheet
        open={moreSheetOpen}
        onClose={() => setMoreSheetOpen(false)}
      />
      {templateSelectorOpen && (
        <Suspense fallback={null}>
          <TemplateSelector
            isOpen={templateSelectorOpen}
            onClose={() => setTemplateSelectorOpen(false)}
            onSelect={() => setTemplateSelectorOpen(false)}
          />
        </Suspense>
      )}
    </>
  );
};

/** 페이지별 ErrorBoundary 래퍼 헬퍼 */
function wrapPage(element: React.ReactElement, name: string) {
  return <ErrorBoundary pageName={name}>{element}</ErrorBoundary>;
}

/**
 * 앱 메인 컴포넌트
 */
const App: React.FC = () => {
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setShowShortcuts(true);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 transition-colors">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:text-sm"
        >
          본문으로 건너뛰기
        </a>
        <Header />
        <OfflineBanner />
        <main id="main-content">
          {/* 최후의 보루: 전역 ErrorBoundary */}
          <ErrorBoundary>
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-screen">
              <Spinner size="lg" />
            </div>
          }>
          <AppShell>
          <Routes>
            {/* 루트 경로: 로그인 상태에 따라 리다이렉트 */}
            <Route path="/" element={<RootRedirect />} />

            {/* 공개 라우트 */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/shared/:token" element={
              wrapPage(<SharedAlbumPage />, '공유 앨범')
            } />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/verify-email/:token" element={<EmailVerifyPage />} />
            <Route path="/gallery" element={
              wrapPage(<GalleryPage />, '갤러리')
            } />
            <Route path="/gallery/:id" element={
              wrapPage(<GalleryDetailPage />, '갤러리 상세')
            } />
            <Route path="/albums/bookmarks/:id" element={
              <ProtectedRoute>{wrapPage(<BookmarkDetailPage />, '북마크 상세')}</ProtectedRoute>
            } />
            <Route path="/help" element={
              wrapPage(<HelpPage />, '도움말')
            } />
            <Route path="/user/:id" element={
              wrapPage(<PublicProfilePage />, '사용자 프로필')
            } />
            <Route path="/guide" element={<Navigate to="/help" replace />} />

            {/* 데모 → 로그인으로 리다이렉트 (기존 URL 호환) */}
            <Route path="/demo" element={<Navigate to="/login" replace />} />

            {/* 보호된 라우트: 로그인 필요 */}
            <Route
              path="/albums"
              element={
                <ProtectedRoute>
                  {wrapPage(<AlbumsPage />, '내 앨범')}
                </ProtectedRoute>
              }
            />
            {/* 신규 앨범 컨텍스트 라우트 */}
            <Route
              path="/albums/new"
              element={
                <ProtectedRoute>
                  {wrapPage(<EditorPage />, '에디터')}
                </ProtectedRoute>
              }
            />
            <Route
              path="/albums/:id/edit"
              element={
                <ProtectedRoute>
                  {wrapPage(<EditorPage />, '에디터')}
                </ProtectedRoute>
              }
            />
            <Route
              path="/albums/:id/export"
              element={
                <ProtectedRoute>
                  {wrapPage(<ExportPage />, '내보내기')}
                </ProtectedRoute>
              }
            />
            {/* 기존 경로 호환 — /albums/new로 리다이렉트 */}
            <Route path="/editor" element={<Navigate to="/albums/new" replace />} />
            <Route path="/export" element={<Navigate to="/albums/new" replace />} />
            <Route
              path="/pricing"
              element={
                <ProtectedRoute>
                  {wrapPage(<PricingPage />, '요금제')}
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription/success"
              element={
                <ProtectedRoute>
                  {wrapPage(<SubscriptionSuccessPage />, '구독 완료')}
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription/cancel"
              element={
                <ProtectedRoute>
                  {wrapPage(<SubscriptionCancelPage />, '구독 취소')}
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  {wrapPage(<ProfilePage />, '프로필')}
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />

            {/* 공개 라우트: 템플릿 마켓 */}
            <Route path="/templates" element={wrapPage(<TemplatesPage />, '템플릿 마켓')} />
            <Route path="/templates/:id" element={wrapPage(<TemplateDetailPage />, '템플릿 상세')} />
            {/* 보호된 라우트: 템플릿 업로드 (로그인 필요) */}
            <Route
              path="/templates/upload"
              element={
                <ProtectedRoute>
                  {wrapPage(<TemplateUploadPage />, '템플릿 공유')}
                </ProtectedRoute>
              }
            />

            {/* 보호된 라우트: 신규 페이지 */}
            <Route path="/teams" element={<ProtectedRoute>{wrapPage(<TeamsPage />, '팀 관리')}</ProtectedRoute>} />
            <Route path="/credits" element={<ProtectedRoute>{wrapPage(<CreditsPage />, '크레딧')}</ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute>{wrapPage(<NotificationsPage />, '알림')}</ProtectedRoute>} />
            <Route path="/feed" element={<ProtectedRoute>{wrapPage(<FeedPage />, '피드')}</ProtectedRoute>} />
            <Route path="/migration" element={<ProtectedRoute>{wrapPage(<MigrationPage />, '마이그레이션')}</ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute>{wrapPage(<SettingsPage />, '설정')}</ProtectedRoute>} />
            <Route path="/collections" element={<ProtectedRoute>{wrapPage(<CollectionsPage />, '컬렉션')}</ProtectedRoute>} />
            <Route path="/collections/:id" element={wrapPage(<CollectionDetailPage />, '컬렉션 상세')} />
            <Route path="/bookmarks" element={<ProtectedRoute>{wrapPage(<BookmarksPage />, '내 북마크')}</ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute requireRole="admin">{wrapPage(<AdminPage />, '관리자')}</ProtectedRoute>} />

            {/* 404 에러 페이지 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </AppShell>
          </Suspense>
          </ErrorBoundary>
        </main>
        <ToastContainer />
        <PWAInstaller />
        <ShortcutsHelp isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
      </div>
    </ThemeProvider>
  );
};

export default App;
